/**
 * Floating Dropdown System for Desktop Header Menus
 * 
 * Replaces Shopify's default <details> dropdown behavior on desktop
 * with a premium floating dropdown system.
 * 
 * Constraints:
 * - Desktop only (min-width: 990px)
 * - Does not affect mobile behavior
 * - Does not affect Giveaways dropdown
 */

(function() {
  'use strict';

  const DESKTOP_BREAKPOINT = 990;
  const DROPDOWN_GAP = 12; // Gap between trigger and dropdown in pixels (increased for premium feel)
  const ANIMATION_DURATION = 200; // milliseconds (matches CSS)
  const HOVER_OPEN_DELAY = 150; // Delay before opening on hover (ms)
  const HOVER_CLOSE_DELAY = 200; // Delay before closing on mouse leave (ms)

  class FloatingDropdown {
    constructor() {
      this.container = document.getElementById('floating-dropdown');
      this.content = this.container?.querySelector('.floating-dropdown__content');
      this.currentTrigger = null;
      this.isOpen = false;
      this.isDesktop = this.checkIsDesktop();
      this.closeTimeout = null;
      this.openTimeout = null;
      this.hoveredTrigger = null;

      if (!this.container || !this.content) {
        console.warn('Floating dropdown container not found');
        return;
      }

      this.init();
    }

    checkIsDesktop() {
      return window.innerWidth >= DESKTOP_BREAKPOINT;
    }

    init() {
      // Always set up close handlers (they check isDesktop internally)
      this.setupCloseHandlers();

      // Handle window resize
      window.addEventListener('resize', this.handleResize.bind(this));

      // Only initialize dropdowns on desktop
      if (this.isDesktop) {
        // Find all desktop menu triggers (non-Giveaways, non-mobile)
        this.setupTriggers();

        // Prevent Shopify's default behavior for desktop menus
        this.disableShopifyDefaults();
      }
    }

    setupTriggers() {
      // Find all header-menu elements that are NOT Giveaways and NOT in mobile drawer
      // Use data-desktop-floating attribute to identify eligible menus
      // Also try without the attribute as fallback
      let headerMenus = document.querySelectorAll('header-menu[data-desktop-floating]');
      
      // Fallback: if no menus found with attribute, try all header-menu elements
      if (headerMenus.length === 0) {
        headerMenus = document.querySelectorAll('header-menu');
        console.log('FloatingDropdown: No menus with data-desktop-floating found, trying all header-menu elements');
      }
      
      console.log('FloatingDropdown: Found', headerMenus.length, 'header menus');
      
      let setupCount = 0;
      
      headerMenus.forEach((headerMenu) => {
        // Skip if inside mobile drawer
        if (headerMenu.closest('header-drawer, menu-drawer')) {
          console.log('FloatingDropdown: Skipping menu in mobile drawer');
          return;
        }

        // Skip Giveaways menu (it has its own hover system)
        const link = headerMenu.closest('li');
        const giveawaysLink = link && (link.querySelector('a[href*="giveaways"]') || link.textContent.toLowerCase().includes('giveaway'));
        if (giveawaysLink) {
          console.log('FloatingDropdown: Skipping Giveaways menu');
          return;
        }

        const details = headerMenu.querySelector('details.mega-menu');
        if (!details) {
          console.log('FloatingDropdown: No details.mega-menu found in', headerMenu);
          return;
        }

        const summary = details.querySelector('summary');
        if (!summary) {
          console.log('FloatingDropdown: No summary found in', details);
          return;
        }

        // Skip if already set up
        if (summary.hasAttribute('data-floating-trigger')) {
          console.log('FloatingDropdown: Already set up for', summary.textContent.trim());
          return;
        }

        // Mark this as a floating dropdown trigger
        summary.setAttribute('data-floating-trigger', 'true');
        details.setAttribute('data-floating-menu', 'true');

        const menuTitle = summary.textContent.trim();
        console.log('FloatingDropdown: Setting up trigger for', menuTitle);

        // Add click handler - use capture phase to run before Shopify's handlers
        const clickHandler = (e) => {
          console.log('FloatingDropdown: Click detected on', menuTitle);
          this.handleTriggerClick(e, summary, details);
        };
        
        summary.addEventListener('click', clickHandler, true); // Use capture phase
        
        // Add hover handlers
        summary.addEventListener('mouseenter', (e) => {
          this.handleTriggerHover(e, summary, details);
        });
        
        summary.addEventListener('mouseleave', (e) => {
          this.handleTriggerLeave(e, summary);
        });
        
        setupCount++;
      });
      
      console.log('FloatingDropdown: Successfully set up', setupCount, 'triggers');
    }

    disableShopifyDefaults() {
      // Intercept Shopify's default dropdown behavior for desktop floating menus
      const floatingMenus = document.querySelectorAll('details[data-floating-menu]');
      
      floatingMenus.forEach((details) => {
        const summary = details.querySelector('summary');
        if (!summary) return;

        // Prevent the details element from opening via native behavior on desktop
        const observer = new MutationObserver((mutations) => {
          if (!this.isDesktop) return;
          if (details.closest('header-drawer, menu-drawer')) return;
          
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
              // If Shopify tries to open it via native behavior, prevent it
              // Only allow it if our floating dropdown is managing it
              if (details.hasAttribute('open') && this.currentTrigger !== summary) {
                // Delay slightly to allow our handler to run first
                requestAnimationFrame(() => {
                  if (details.hasAttribute('open') && this.currentTrigger !== summary) {
                    details.removeAttribute('open');
                  }
                });
              }
            }
          });
        });

        observer.observe(details, {
          attributes: true,
          attributeFilter: ['open']
        });
      });
    }

    handleTriggerClick(e, summary, details) {
      // Only handle on desktop
      if (!this.isDesktop) {
        console.log('FloatingDropdown: Not desktop, skipping');
        return;
      }

      // Skip if in mobile drawer
      if (summary.closest('header-drawer, menu-drawer')) {
        console.log('FloatingDropdown: In mobile drawer, skipping');
        return;
      }

      // Cancel any pending hover open
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }

      // Prevent default <details> behavior and stop propagation
      e.preventDefault();
      e.stopImmediatePropagation(); // Use stopImmediatePropagation to prevent other handlers

      console.log('FloatingDropdown: Handling click, isOpen:', this.isOpen, 'currentTrigger:', this.currentTrigger === summary);

      // Toggle dropdown
      if (this.currentTrigger === summary && this.isOpen) {
        console.log('FloatingDropdown: Closing dropdown');
        this.close();
      } else {
        console.log('FloatingDropdown: Opening dropdown');
        this.open(summary, details);
      }
    }

    handleTriggerHover(e, summary, details) {
      // Only handle on desktop
      if (!this.isDesktop) return;
      if (summary.closest('header-drawer, menu-drawer')) return;

      this.hoveredTrigger = summary;

      // Cancel any pending close
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      // If already open for this trigger, do nothing
      if (this.currentTrigger === summary && this.isOpen) {
        return;
      }

      // Schedule opening after delay
      this.openTimeout = setTimeout(() => {
        // Double-check we're still hovering
        if (this.hoveredTrigger === summary && this.isDesktop) {
          console.log('FloatingDropdown: Opening on hover');
          this.open(summary, details);
        }
        this.openTimeout = null;
      }, HOVER_OPEN_DELAY);
    }

    handleTriggerLeave(e, summary) {
      // Only handle on desktop
      if (!this.isDesktop) return;

      // Cancel any pending open
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }

      // If this was the hovered trigger, clear it
      if (this.hoveredTrigger === summary) {
        this.hoveredTrigger = null;
      }

      // If dropdown is open for this trigger, schedule close after delay
      // This allows time for mouse to move to dropdown
      if (this.currentTrigger === summary && this.isOpen) {
        // Check if mouse is moving to dropdown
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && this.container.contains(relatedTarget)) {
          // Mouse is moving to dropdown, don't close
          return;
        }

        // Schedule close after delay
        this.closeTimeout = setTimeout(() => {
          // Only close if still not hovering over dropdown or trigger
          if (!this.hoveredTrigger && this.isDesktop && this.isOpen) {
            this.close();
          }
          this.closeTimeout = null;
        }, HOVER_CLOSE_DELAY);
      }
    }

    open(summary, details) {
      // Close any existing dropdown
      if (this.isOpen) {
        this.close();
      }

      // Cancel any pending timeouts
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      this.currentTrigger = summary;
      this.isOpen = true;

      // Get dropdown content from the details element
      const dropdownContent = details.querySelector('.mega-menu__content');
      if (!dropdownContent) {
        console.error('FloatingDropdown: No .mega-menu__content found');
        return;
      }

      console.log('FloatingDropdown: Opening dropdown with content');

      // Clone the content (don't move it, as it's needed for mobile)
      const contentClone = dropdownContent.cloneNode(true);
      contentClone.removeAttribute('id'); // Remove any IDs to avoid duplicates
      
      // Clear and populate floating container
      this.content.innerHTML = '';
      this.content.appendChild(contentClone);

      // Calculate position
      this.positionDropdown(summary);

      // Show dropdown with animation
      this.container.setAttribute('aria-hidden', 'false');
      this.container.classList.add('is-open');
      
      // Add active state to trigger
      summary.classList.add('is-active');
      summary.setAttribute('aria-expanded', 'true');

      // Add hover handlers to dropdown to keep it open
      this.setupDropdownHover();

      console.log('FloatingDropdown: Dropdown opened successfully');
    }

    setupDropdownHover() {
      // Remove existing handlers if any
      this.container.removeEventListener('mouseenter', this.dropdownMouseEnter);
      this.container.removeEventListener('mouseleave', this.dropdownMouseLeave);

      // Add handlers to keep dropdown open when hovering over it
      this.dropdownMouseEnter = () => {
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = null;
        }
      };

      this.dropdownMouseLeave = () => {
        // Close after delay when mouse leaves dropdown
        if (this.isDesktop && this.isOpen) {
          this.closeTimeout = setTimeout(() => {
            // Only close if mouse is not over trigger
            if (!this.hoveredTrigger && this.isDesktop) {
              this.close();
            }
            this.closeTimeout = null;
          }, HOVER_CLOSE_DELAY);
        }
      };

      this.container.addEventListener('mouseenter', this.dropdownMouseEnter);
      this.container.addEventListener('mouseleave', this.dropdownMouseLeave);
    }

    positionDropdown(trigger) {
      const rect = trigger.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;

      // Calculate position
      const top = rect.bottom + scrollY + DROPDOWN_GAP;
      const left = rect.left + scrollX;

      // Apply position
      this.container.style.top = `${top}px`;
      this.container.style.left = `${left}px`;

      // Ensure dropdown doesn't go off-screen
      requestAnimationFrame(() => {
        const containerRect = this.container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Adjust if too far right
        if (containerRect.right > viewportWidth) {
          const overflow = containerRect.right - viewportWidth;
          this.container.style.left = `${Math.max(0, left - overflow)}px`;
        }

        // Adjust if too far left
        if (containerRect.left < 0) {
          this.container.style.left = '0px';
        }

        // Adjust if too far down (show above instead)
        if (containerRect.bottom > viewportHeight && rect.top > containerRect.height) {
          this.container.style.top = `${rect.top + scrollY - containerRect.height - DROPDOWN_GAP}px`;
        }
      });
    }

    close() {
      if (!this.isOpen) return;

      // Cancel any pending timeouts
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
        this.openTimeout = null;
      }
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      this.isOpen = false;
      this.container.classList.remove('is-open');
      this.container.setAttribute('aria-hidden', 'true');

      // Remove active state from all triggers
      document.querySelectorAll('[data-floating-trigger].is-active').forEach((trigger) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      });

      // Remove dropdown hover handlers
      if (this.dropdownMouseEnter) {
        this.container.removeEventListener('mouseenter', this.dropdownMouseEnter);
      }
      if (this.dropdownMouseLeave) {
        this.container.removeEventListener('mouseleave', this.dropdownMouseLeave);
      }

      this.currentTrigger = null;
      this.hoveredTrigger = null;
      this.content.innerHTML = '';
    }

    setupCloseHandlers() {
      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!this.isOpen) return;
        if (!this.isDesktop) return;

        // Don't close if clicking inside the dropdown
        if (this.container.contains(e.target)) {
          return;
        }

        // Don't close if clicking on a trigger (handled by handleTriggerClick)
        if (e.target.closest('[data-floating-trigger]')) {
          return;
        }

        // Don't close if clicking inside header-menu (but not the trigger)
        const headerMenu = e.target.closest('header-menu');
        if (headerMenu && headerMenu.querySelector('[data-floating-trigger]')) {
          // Only close if clicking outside the menu structure
          const clickedTrigger = e.target.closest('[data-floating-trigger]');
          if (!clickedTrigger) {
            this.close();
          }
          return;
        }

        // Close for all other clicks
        this.close();
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (!this.isDesktop) return;
        if (e.key === 'Escape' || e.keyCode === 27) {
          this.close();
          if (this.currentTrigger) {
            this.currentTrigger.focus();
          }
        }
      });

      // Close on resize to mobile
      window.addEventListener('resize', () => {
        if (!this.checkIsDesktop() && this.isOpen) {
          this.close();
        }
      });
    }

    handleResize() {
      const wasDesktop = this.isDesktop;
      this.isDesktop = this.checkIsDesktop();

      // If switching from desktop to mobile, close dropdown
      if (wasDesktop && !this.isDesktop && this.isOpen) {
        this.close();
      }

      // If switching to desktop, reinitialize
      if (!wasDesktop && this.isDesktop) {
        this.setupTriggers();
        this.disableShopifyDefaults();
      }

      // Reposition if open
      if (this.isOpen && this.isDesktop && this.currentTrigger) {
        this.positionDropdown(this.currentTrigger);
      }
    }
  }

  // Initialize when DOM is ready - wait for all scripts to load
  function initFloatingDropdown() {
    // Wait a bit to ensure Shopify's global.js has run and DOM is fully ready
    setTimeout(() => {
      console.log('FloatingDropdown: Initializing...', {
        readyState: document.readyState,
        isDesktop: window.innerWidth >= 990,
        container: document.getElementById('floating-dropdown')
      });
      
      const instance = new FloatingDropdown();
      
      // Also try to set up triggers after a short delay in case elements weren't ready
      if (window.innerWidth >= 990) {
        setTimeout(() => {
          if (instance && !instance.isOpen) {
            console.log('FloatingDropdown: Retrying setup...');
            instance.setupTriggers();
            instance.disableShopifyDefaults();
          }
        }, 500);
      }
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFloatingDropdown);
  } else {
    initFloatingDropdown();
  }
})();

